import { lib } from '@giscube/geoportal'

const L = lib.L

function isPointOnSegment (p, a, b, tolerance = 1e-6) {
  // vector cross product = 0
  const cross = (p.lat - a.lat) * (b.lng - a.lng) - (p.lng - a.lng) * (b.lat - a.lat)

  if (Math.abs(cross) > tolerance) return false

  const dot = (p.lng - a.lng) * (b.lng - a.lng) + (p.lat - a.lat) * (b.lat - a.lat)

  if (dot < 0) return false

  const lenSq = (b.lng - a.lng) ** 2 + (b.lat - a.lat) ** 2

  return dot <= lenSq
}

function splitPolylineByPoints (linestringLatlngs, projectedCoordinateSeconds, reverse) {
  let result = []
  let current = []

  for (let i = 0; i < linestringLatlngs.length - 1; i++) {
    const a = linestringLatlngs[i]
    const b = linestringLatlngs[i + 1]

    if (current.length === 0) {
      current.push({
        latLng: a,
        second: -9999
      })
    }

    projectedCoordinateSeconds.forEach(p => {
      if (isPointOnSegment(p.latLng, a, b)) {
        current.push(p)
        result.push(current)
        current = [p]
      }
    })

    current.push({
      latLng: b,
      second: -9999
    })
  }

  if (current.length > 1) result.push(current)

  let initialDistance = 0
  if (result.length > 0 && result[0][0].second === -9999) {
    const firstPiece = result.shift()
    initialDistance = getLineLength(firstPiece.map(fp => fp.latLng))
  }
  if (result.length > 0 && result.at(-1).at(-1).second === -9999) {
    result.pop()
  }
  result = result.map(linePiece => setTimeToVertices(linePiece))
  return { splittedLines: result, initialDistance }
}

function setTimeToVertices (vertices) {
  // convertir coords a LatLng
  const pts = vertices.map(v =>
    L.latLng(v.latLng.lat, v.latLng.lng)
  )
  const t0 = vertices[0].second
  const t1 = vertices.at(-1).second

  const dist = [0]
  let totalDistance = 0

  // distàncies acumulades dins del tram
  for (let i = 0; i < vertices.length - 1; i++) {
    totalDistance += pts[i].distanceTo(pts[i + 1])
    dist.push(totalDistance)
  }

  const velocity = totalDistance / (t1 - t0)

  for (let i = 1; i < vertices.length - 1; i++) {
    vertices[i].second = t0 + dist[i] / velocity
    vertices[i].distance = vertices[0].distance + dist[i]
  }

  return vertices
}

function deleteDuplicatesCoordinatesSeconds (coordinatesSeconds) {
  return coordinatesSeconds.reduce((result, currValue) => {
    if (result.filter(({ second }) => currValue.second === second).length === 0) {
      result.push(currValue)
    }
    return result
  }, [])
}

function dot (a, b) {
  return a.x * b.x + a.y * b.y
}

function projectOnSegmentWithT (p, a, b) {
  const ab = b.subtract(a)
  const ap = p.subtract(a)

  const ab2 = dot(ab, ab)
  if (ab2 === 0) {
    return { point: a, t: 0 }
  }

  const t = Math.max(0, Math.min(1, dot(ap, ab) / ab2))
  return {
    point: a.add(ab.multiplyBy(t)),
    t
  }
}

function projectPointAndDistanceAlongLine (map, latlng, lineLatLngs) {
  const p = map.project(latlng)

  let minDist = Infinity
  let closest = null
  let distanceAlong = 0

  let accumulated = 0

  for (let i = 0; i < lineLatLngs.length - 1; i++) {
    const a = map.project(lineLatLngs[i])
    const b = map.project(lineLatLngs[i + 1])

    const segmentLength = a.distanceTo(b)

    const { point, t } = projectOnSegmentWithT(p, a, b)
    const distToSegment = p.distanceTo(point)

    if (distToSegment < minDist) {
      minDist = distToSegment
      closest = point
      distanceAlong = accumulated + t * segmentLength
    }

    accumulated += segmentLength
  }

  return {
    latLng: map.unproject(closest),
    distanceAlong // en unitats del CRS (metres en EPSG:3857)
  }
}

function shouldReverseLine (map, lineLatLngs, coordinatesSeconds) {
  const firstPoint = L.latLng(
    coordinatesSeconds[0].coordinates[1],
    coordinatesSeconds[0].coordinates[0]
  )
  const lastPoint = L.latLng(
    coordinatesSeconds[coordinatesSeconds.length - 1].coordinates[1],
    coordinatesSeconds[coordinatesSeconds.length - 1].coordinates[0]
  )
  const first = projectPointAndDistanceAlongLine(map, firstPoint, lineLatLngs)
  const last = projectPointAndDistanceAlongLine(map, lastPoint, lineLatLngs)

  return first.distanceAlong > last.distanceAlong
}

function projectCoordinatesSecondsToLinestring (lineLatLngs, coordinatesSeconds, map) {
  return coordinatesSeconds.map(coordSec => {
    const latLng = L.latLng(coordSec.coordinates[1], coordSec.coordinates[0])
    const closestLatLng = projectPointAndDistanceAlongLine(map, latLng, lineLatLngs)

    return {
      latLng: closestLatLng.latLng,
      distance: closestLatLng.distanceAlong,
      second: coordSec.second
    }
  })
}

function mergeLines (lines) {
  if (lines.length === 0) {
    return []
  }
  const firstLinePoints = lines.shift()
  const mergedLines = lines.reduce((result, currLine) => {
    currLine.shift()
    return [...result, ...currLine]
  }, firstLinePoints)
  return mergedLines.map(l => {
    return {
      latLng: l.latLng,
      second: l.second,
      distance: l.distance
    }
  })
}

function getLineLength (lineLatLngs) {
  let distance = 0
  for (let i = 0; i < lineLatLngs.length - 1; i++) {
    const p0 = lineLatLngs[i]
    const p1 = lineLatLngs[i + 1]
    distance += p0.distanceTo(p1)
  }
  return distance
}

function fixDistances (line, initialDistance, totalDistance, reverse, map) {
  line[0].distance = initialDistance
  for (let i = 0; i < line.length - 1; i++) {
    const p0 = line[i].latLng
    const p1 = line[i + 1].latLng
    line[i + 1].distance = line[i].distance + p0.distanceTo(p1)
  }
  if (reverse) {
    return line.map(l => {
      return {
        latLng: l.latLng,
        second: l.second,
        distance: totalDistance - l.distance
      }
    })
  } else {
    return line
  }
}

export function interpolate (linestring, coordinatesSeconds, map) {
  if (!linestring || !coordinatesSeconds || coordinatesSeconds.length === 0) {
    return []
  }
  const uniqueCoordinatesSeconds = deleteDuplicatesCoordinatesSeconds(coordinatesSeconds)
  const lineLatLngs = linestring.getLatLngs()
  const reversed = shouldReverseLine(map, lineLatLngs, coordinatesSeconds)
  if (reversed) {
    lineLatLngs.reverse()
  }
  const projectedCoordinatesSeconds = projectCoordinatesSecondsToLinestring(lineLatLngs, uniqueCoordinatesSeconds, map)
  const { splittedLines, initialDistance } = splitPolylineByPoints(lineLatLngs, projectedCoordinatesSeconds, reversed)
  const singleLine = mergeLines(splittedLines)
  const totalDistance = getLineLength(lineLatLngs)
  const fixedDistances = fixDistances(singleLine, initialDistance, totalDistance, reversed, map)
  return fixedDistances.map(point => {
    return {
      coordinates: [point.latLng.lng, point.latLng.lat],
      second: point.second,
      distance: point.distance,
      reversed
    }
  })
}
